/* Shop by stone — the endless loop, the arrows, and which stone counts as
 * "the one in the middle".
 *
 * THE LOOP
 * The whole row of stones is duplicated once before and once after the real
 * ones, and the row is kept away from the hard ends of the scroll range by
 * always having at least one stone of runway on each side. When a step would
 * run out of runway, the row is first teleported by exactly one set width in
 * the opposite direction — the same stones land on the same pixels, so the
 * teleport is invisible — and the step is then animated from there. Clicking
 * right at the last stone therefore keeps sliding left and the first one comes
 * round again; the arrows go the other way just as endlessly.
 *
 * Three copies are the minimum that works: with two, you would run out of
 * clones on one side while the other side is still needed.
 *
 * WHY THE RUNWAY, NOT A FIXED SET-WIDE WINDOW
 * "Stay inside the middle copy" sounds tidier but puts the wrap boundary at
 * exactly scrollLeft === setWidth — which is also the starting position. A
 * scrollLeft assigned as a fraction comes back from the browser rounded, so the
 * check flipped between true and false on every scroll event, and the row sat
 * there oscillating between two positions forever. Measuring the runway from the
 * physical ends instead, and teleporting only when a step actually needs it,
 * cannot get stuck: the teleport always moves the row a whole set away from the
 * boundary.
 *
 * THE CLONES
 * They are decoration. They are aria-hidden and their links are taken out of
 * the tab order, so a screen reader or a keyboard user meets the eight real
 * stones once and never gets trapped in a loop of duplicates. Shopify's editor
 * block attributes are stripped from them too, or the editor sees every block
 * twice.
 *
 * Written as a custom element rather than a plain script so the theme editor can
 * re-render the section as many times as it likes: the definition only happens
 * once, and any stone-carousel element that appears later — including ones the
 * editor creates — gets upgraded automatically.
 *
 * Everything degrades: with no JS the row is still a normal scroll-snap list the
 * customer can swipe, and the stones all render at the same size.
 */
(function () {
  if (!window.customElements || customElements.get('stone-carousel')) return;

  /* Rounding slack: scrollLeft assigned as a fraction comes back rounded, so a
     bare comparison against a computed edge can land on the wrong side. */
  const SLACK = 2;

  /* Real stones only — used once, before anything has been cloned. */
  const REAL_ITEMS = '[data-stone-item]:not([data-stone-clone])';

  class StoneCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-stone-track]');
      if (!this.track) return;

      this.real = Array.prototype.slice.call(this.querySelectorAll(REAL_ITEMS));
      this.items = this.real;
      this.setWidth = 0;
      this.step = 0;

      this.prev = this.querySelector('[data-stone-prev]');
      this.next = this.querySelector('[data-stone-next]');

      this.onScroll = () => this.handleScroll();
      this.onResize = () => {
        this.measure();
        this.sync();
      };
      this.onStep = (event) => this.stepBy(event.currentTarget === this.prev);

      this.buildLoop();

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      if (this.prev) this.prev.addEventListener('click', this.onStep);
      if (this.next) this.next.addEventListener('click', this.onStep);

      this.sync();
      /* Web fonts and the lazy images can change the row width after first paint,
         so re-measure once things have settled. */
      this.settleTimer = setTimeout(this.onResize, 400);
    }

    disconnectedCallback() {
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.prev) this.prev.removeEventListener('click', this.onStep);
      if (this.next) this.next.removeEventListener('click', this.onStep);
      clearTimeout(this.settleTimer);
    }

    /* ------------------------------------------------------------------ loop */

    buildLoop() {
      const track = this.track;
      /* Guarded: the theme editor can hand the same element back without
         rebuilding it, and a second set of clones would double the row. */
      if (!this.real.length || track.dataset.stoneLoop === '1') return;
      track.dataset.stoneLoop = '1';

      if (this.real.length > 1) {
        const before = document.createDocumentFragment();
        const after = document.createDocumentFragment();
        this.real.forEach((item) => {
          before.appendChild(this.decorateClone(item));
          after.appendChild(this.decorateClone(item));
        });
        track.insertBefore(before, track.firstChild);
        track.appendChild(after);
      }

      this.items = Array.prototype.slice.call(track.querySelectorAll('[data-stone-item]'));
      this.measure(true);
    }

    decorateClone(item) {
      const clone = item.cloneNode(true);
      clone.setAttribute('data-stone-clone', 'true');
      clone.setAttribute('aria-hidden', 'true');
      /* The editor ties its block ids to these attributes; a second copy of them
         makes it believe the block exists twice. */
      Array.prototype.slice.call(clone.attributes).forEach((attr) => {
        if (attr.name.indexOf('data-shopify') === 0) clone.removeAttribute(attr.name);
      });
      /* Out of the tab order, so a keyboard user walks the eight real stones
         once instead of eight plus sixteen copies. */
      Array.prototype.slice.call(clone.querySelectorAll('a, button')).forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });
      return clone;
    }

    /* ----------------------------------------------------------------- sizing */

    measure(reposition) {
      if (!this.real.length) return;

      let setWidth = 0;
      this.real.forEach((item) => {
        setWidth += item.getBoundingClientRect().width;
      });
      this.step = this.real[0].getBoundingClientRect().width;
      this.setWidth = setWidth;

      /* First run: land on the first real stone, which sits exactly one set in
         from the left edge of the three copies. */
      if (reposition) this.setScroll(setWidth);
      else this.nudge();
    }

    /* Instant, whatever the track's scroll-behavior says. Used for the first
       positioning and for the teleport — neither may be animated, or the shift
       would be visible. */
    setScroll(left) {
      const track = this.track;
      const behaviour = track.style.scrollBehavior;
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = left;
      track.style.scrollBehavior = behaviour;
    }

    /* How far the row may travel before a step in that direction would run out
       of stones. Leaves one stone of runway at each end. */
    limits() {
      const track = this.track;
      const max = track.scrollWidth - track.clientWidth;
      const guard = this.step + SLACK;
      return { min: guard, max: max - guard, maxScroll: max };
    }

    /* Keep the row inside the runway. Every teleport is a whole set width, so
       what is on screen does not change by a single pixel. Used for swipes and
       for a resize, where the row can end up at a hard end with no step to
       trigger the wrap. */
    nudge() {
      const width = this.setWidth;
      if (!width || !this.step) return;

      const track = this.track;
      const left = track.scrollLeft;
      const room = this.limits();
      if (room.max <= room.min) return;   /* not scrollable, nothing to wrap */

      let target = left;
      while (target < room.min) target += width;
      while (target > room.max) target -= width;
      if (target === left) return;

      this.setScroll(target);
    }

    /* ----------------------------------------------------------------- arrows */

    stepBy(back) {
      if (!this.step || !this.setWidth) return;

      const track = this.track;
      const width = this.setWidth;
      const room = this.limits();
      const left = track.scrollLeft;
      let target = left + (back ? -this.step : this.step);

      /* Teleport first when the step has no runway left, so the animation after
         it is a single stone instead of a long slide back across the row. */
      if (target < room.min) {
        this.setScroll(left + width);
        target += width;
      } else if (target > room.max) {
        this.setScroll(left - width);
        target -= width;
      }

      track.scrollTo({ left: target, behavior: 'smooth' });
    }

    handleScroll() {
      if (!this.setWidth) return;
      this.nudge();
      this.sync();
    }

    /* The arrows stay live at both ends, because both ends now lead somewhere.
       They only go grey in the one case where pressing them can do nothing:
       a row short enough to fit without any scrolling at all. */
    sync() {
      const track = this.track;
      const inert = this.real.length < 2 || track.scrollWidth - track.clientWidth <= 1;
      if (this.prev) this.prev.disabled = inert;
      if (this.next) this.next.disabled = inert;

      /* Highlight whichever stone sits closest to the middle of the row. Measured
         with client rects so the answer already accounts for the current scroll
         position — and over every copy, so the highlight follows the stones as
         they come round. */
      const view = track.getBoundingClientRect();
      const middle = view.left + view.width / 2;
      let best = null;
      let bestDistance = Infinity;
      this.items.forEach((item) => {
        const box = item.getBoundingClientRect();
        const distance = Math.abs(box.left + box.width / 2 - middle);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = item;
        }
      });
      this.items.forEach((item) => item.classList.toggle('is-center', item === best));
    }
  }

  customElements.define('stone-carousel', StoneCarousel);
})();
